import { Flex, Typography, Alert, Form } from "antd"
import SvgIcons from "../layout/assets/SvgIcons"
import Wrapper from "../form-editor/Preview/Wrapper";
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import dayjs from "dayjs"
import { spaceColorBgMapping } from "../../utils/colorBgColorMap";
import { isValidDate } from "../../utils/requestHelper";
import { safeDayjs } from "../../utils/safeDate.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { resolveFieldTranslation } from "../../utils/formTranslations";
import { trimStringValues } from "../../utils/trimStrings";
import { READS_ALL_FORM_VALUES, fieldValueDeps, flattenFormItems, getAllWidgets, getPreviousStepFrozenFields } from "../../utils/formsHelper";

const { Title } = Typography;

const EMPTY_ARR = [];

const RequestForm = (props) => {
    const { spaceData, forms, currentStatus, conditions, requestDetail, defaultRequestDetail, isSubmitted, submitRequest, setIsSubmitted, passFormValues, requestForm, spaceEntitlements = [], disableAllFields = false } = props
    const { userInfo } = useSelector(state => state.user)
    const { i18n } = useTranslation()

    // `preserve: true` reads the whole form store rather than the registered-field
    // snapshot. A hidden field is unmounted, so it drops out of the plain snapshot -
    // a condition that reads that field would then see it as empty and stop firing,
    // even though the form still holds its value. Conditions have to be evaluated
    // against the full store.
    const lastAppliedIdentityRef = useRef("")
    let formValues = Form.useWatch([], { form: requestForm, preserve: true });
    const [freezeFields, setFreezeFields] = useState({})
    const [formDefaultValues, setFormDefaultValues] = useState(null)

    // Check if there are multiple cycles and current view is not the current cycle
    const hasMultipleCycles = defaultRequestDetail?.logs && Object.keys(defaultRequestDetail.logs).length > 0
    const isNonCurrentCycleView = hasMultipleCycles && requestDetail?.currentCycle !== defaultRequestDetail?.currentCycle

    // "Allow forms reversal" on the space lists the approved forms newest-first in
    // this panel. Display only - `forms` keeps its workflow order everywhere else
    // (default values, submission payload, scoring, print).
    const allowFormsReversal = spaceData?.allowFormsReversal === true || spaceData?.allowFormsReversal === "true"
    const orderedForms = allowFormsReversal ? [...forms].reverse() : forms

    // Grouped once instead of re-filtering inside the render map, so each form's
    // condition array keeps a stable identity for the memo and slice below.
    const conditionsByForm = useMemo(() => {
        const map = new Map()
        for (const condition of conditions || []) {
            if (!map.has(condition.formId)) map.set(condition.formId, [])
            map.get(condition.formId).push(condition)
        }
        return map
    }, [conditions])

    const allFormElements = useMemo(
        () => flattenFormItems(forms),
        [forms]
    )

    // Each field gets only the values it can read - its own, whatever its config names,
    // and whatever the conditions targeting it reference. `formValues` changes identity
    // on every keystroke, so handing every field the whole store re-renders the entire
    // form per character; the slice keeps that to the fields actually affected.
    const sliceCacheRef = useRef(new Map())
    const formValuesFor = (data, formConditions) => {
        if (READS_ALL_FORM_VALUES.has(data.formType)) return formValues
        const keys = fieldValueDeps(data, formConditions)
        const values = keys.map(k => formValues?.[k])
        const cached = sliceCacheRef.current.get(data.id)
        if (cached && cached.keys.length === keys.length &&
            cached.keys.every((k, i) => k === keys[i]) &&
            cached.values.every((v, i) => v === values[i])) {
            return cached.slice
        }
        const slice = {}
        keys.forEach((k, i) => { slice[k] = values[i] })
        sliceCacheRef.current.set(data.id, { keys, values, slice })
        return slice
    }

    useEffect(() => {
        if (formValues) {
            // Read the full store (getFieldsValue(true)) instead of the useWatch snapshot so
            // imperatively-set fields like table rows (set via form.setFieldValue, which does not
            // fire onValuesChange or reliably update useWatch) are included in the payload.
            passFormValues(requestForm.getFieldsValue(true))
        }
    }, [formValues])

    const valueModifier = (_values) => {
        let result = {}
        let values = { ..._values } || {}
        if (typeof _values == "string") {
            try {
                values = JSON.parse(_values)
                if (typeof values != "object") {
                    values = {}
                }
            } catch (error) {

            }
        }
        Object.keys(values).map(key => {
            var formats = [
                moment.ISO_8601,
                "MM/DD/YYYY  :)  HH*mm*ss"
            ];

            let isDate = isValidDate(values[key]);
            //  values[key] ? moment(values[key], formats, true)?.isValid() : false
            if (values[key] && isDate) {
                result = { ...result, [key]: safeDayjs(values[key]) }
            } else if (Array.isArray(values[key])) {
                let arrValue = values[key]
                arrValue = arrValue.map((value, index) => {
                    let reqData = {}
                    if (typeof value !== "string") {
                        for (const key in value) {
                            let currentValue = value[key]
                            // Table cells: a rehydrated dayjs still reports
                            // $isDayjsObject but has no methods, so it has to be
                            // rebuilt rather than passed to dayjs().
                            if (currentValue?.$isDayjsObject || isValidDate(currentValue)) {
                                currentValue = safeDayjs(currentValue)
                            }
                            reqData[key] = currentValue
                        }
                        // No row `key` here: it is presentational, and TableInput
                        // adds it when it derives its rows from the store.
                        return reqData
                    } else {
                        return value
                    }
                })
                result = { ...result, [key]: arrValue }
            } else if (values[key] && !isDate) {
                result = { ...result, [key]: values[key] }
            }
        })
        return result
    }

    useEffect(() => {
        let defaultValues = {}
        forms.map(form => {
            if (form.defaultValues) {
                defaultValues = { ...defaultValues, ...form.defaultValues }
            }
        })

        const requestIdentity = `${requestDetail?.requestId || ""}-${requestDetail?.currentCycle || ""}`
        const identityChanged = lastAppliedIdentityRef.current !== requestIdentity
        lastAppliedIdentityRef.current = requestIdentity

        if (requestDetail.value) {
            let modValues = valueModifier(requestDetail?.value || {})
            const nextValues = { ...defaultValues, ...modValues }

            // A save echo keeps the same request/cycle. Don't replace a locally longer
            // attachment list with the stale payload from an earlier upload's save.
            if (!identityChanged) {
                const current = requestForm.getFieldsValue(true) || {}
                Object.keys(current).forEach((key) => {
                    const localList = current[key]?.fileList
                    const incomingList = nextValues[key]?.fileList
                    if (Array.isArray(localList) && (!Array.isArray(incomingList) || incomingList.length < localList.length)) {
                        nextValues[key] = current[key]
                    }
                })
            }

            requestForm.resetFields();
            requestForm.setFieldsValue(nextValues)

            if ((requestDetail.workflow?.length === 0 ||
                requestDetail.status === "draft") && (userInfo.isAdmin || defaultRequestDetail?.createdBy?.email === userInfo.email) && (userInfo.isAdmin || spaceEntitlements.includes("Workflow_Documents_Update"))) {
                setFreezeFields({})
            } else {
                // Freeze only what a previous step stored: visible fields and
                // hidden fields a condition set. Hidden-not-set fields stay
                // unfrozen so they can be filled when they become visible.
                setFreezeFields(getPreviousStepFrozenFields(requestDetail?.workflow || []))
            }
        } else {
            requestForm.setFieldsValue({ ...defaultValues })
        }
        setFormDefaultValues(Object.keys(defaultValues).length > 0 ? { ...defaultValues } : null)
    }, [requestDetail, userInfo, forms])

    useEffect(() => {
        if (isSubmitted) {
            requestForm.submit();
            setIsSubmitted(false)
        }
    }, [isSubmitted])

    const submitForm = async () => {
        try {
            // Normalize values first so whitespace-only inputs are treated as empty
            const raw = requestForm.getFieldsValue(true);
            const cleaned = trimStringValues(raw);

            // Coerce number fields to undefined when they're empty strings
            const allElements = getAllWidgets(forms.flatMap(f => f.items || []));
            const numberIds = allElements.filter(e => e.formType === 'numberForm').map(e => e.id);
            numberIds.forEach(id => { if (cleaned[id] === '') cleaned[id] = undefined; });

            await requestForm.setFieldsValue(cleaned);
            await requestForm.validateFields();

            // Use the full store so table rows (set via setFieldValue) are present in the payload.
            submitRequest(cleaned)
        }
        catch {
        }
    }

    const handleFormChange = (changedValues) => {
        setIsSubmitted(false)
    }

    return (
        <Form form={requestForm} onValuesChange={handleFormChange} layout="vertical" onFinish={submitForm} scrollToFirstError={{ behavior: 'smooth', block: 'center' }} key={`${requestDetail?.requestId || "new"}-${requestDetail?.currentCycle || ""}`}>
            {
                orderedForms.map(form => {
                    let headerData = resolveFieldTranslation(form?.formHeader || {}, i18n.language);
                    let elements = form?.items || [];
                    const currentFormConditions = conditionsByForm.get(form?.formId) || EMPTY_ARR

                    return (
                        <div key={form?.formId} className="request-form-section">

                            {
                                !headerData?.hideHeader ?

                                    <Flex vertical align="center" className="form-header">
                                        <Flex style={{ width: "100%" }} gap="1rem">
                                            <div className="icon-box request-approval-form" style={{ borderRadius: "1.5rem", backgroundColor: spaceData.spaceColor ? spaceColorBgMapping[spaceData.spaceColor] : "#f5eeff" }} >
                                                <span style={{ color: spaceData.spaceColor || "#722ed1" }}>
                                                    <SvgIcons type={spaceData.spaceIcon || ""} />
                                                </span>
                                            </div>

                                            <Flex gap={8} vertical style={{ flexGrow: "1" }}>
                                                <Title level={4} style={{ margin: "0" }}>{headerData.label || "Intake Form"}</Title>
                                                {
                                                    headerData.secondaryText && headerData.description && <div className="text-regular">{headerData.description}</div>
                                                }
                                                {
                                                    headerData.info && <Alert message={headerData.formInfo} type="info" showIcon />
                                                }

                                            </Flex>
                                        </Flex>
                                    </Flex>
                                    : null
                            }
                            <div className="droppable-container">
                                {
                                    elements.map(data => {
                                        return <Wrapper currentStatus={currentStatus} spaceData={spaceData} key={data.id} data={data} formElements={elements} allFormElements={allFormElements} form={requestForm} formType={data.formType} conditions={currentFormConditions} previewMode={true} formValues={formValuesFor(data, currentFormConditions)} freezeFields={freezeFields} requestDetail={defaultRequestDetail} defaultRequestDetail={defaultRequestDetail} spaceEntitlements={spaceEntitlements} defaultFormValues={formDefaultValues} disableAllFields={isNonCurrentCycleView || disableAllFields} passFormValues={passFormValues} lng={i18n.language} />
                                    })
                                }
                            </div>
                        </div>
                    )
                })
            }
        </Form>
    )
}

export default React.memo(RequestForm)
